import React, {Fragment, useEffect, useLayoutEffect, useState} from "react";
import {FormContainer} from "../../parts/form-container/FormContainer";
import {Button} from "../../parts/button/Button";
import {Progress} from "../../parts/progress/Progress";
import {areAllValid, createInitialFieldModel, FieldModel} from "../../shared/model/field-model";
import {
    createPostcodeValidator, createRequiredValidator,
    createSurnameValidator, createValidatePolicy
} from "../../helpers/create-validators";
import {createFieldHandlers} from "../../helpers/create-field-handlers";
import {ErrorMessages} from "../../parts/error-messages/ErrorMessages";
import {createElementName} from "../../shared/helpers/element.naming";
import {createInitialPageModel, PageModel, PageStatus} from "../../shared/model/page-model";
import {CollectPolicyAndPostcodeFieldSet} from "./CollectPolicyAndPostcodeFieldSet";
import {createCollectPolicyAndPostcodeRequest} from "./helpers/collectdb-request-helper";
import {isCompleted, isErrored, isProcessing} from "../../helpers/display-block-logic";
import {fromQueryParameters} from "../../helpers/query-parameters-helper";
import {updatePageModelForStatusChangeReducer} from "./helpers/collectdb-page-model-reducers";
import {FormFooter} from "../../parts/form-footer/FormFooter";
import {ANALYTICS_NAMES, sendPageVisitAnalytics} from "../../helpers/analytics-helpers";
import {useLocation} from "react-router-dom";
import {UserSesionModel} from "../../shared/auth/helpers/user-sesion.model";
import {readSignInUserStorage} from "../../shared/helpers/login-session-helpers";
import {checkCollectPolicyAndPostcode, refreshSession, RESPONSE_CODES} from "@simplyhealth/iam_client_lib/lib";
import {updatePageModelReducer} from "../../shared/model/page-model-reduers";
import {createGetCurrentLoginRequest} from "../../helpers/currentlogin-request-helpers";
import {createRedirectUrl} from "../../shared/helpers/login-redirect-helper";
import {createUIMessageAndTitleFromResponseCodes} from "../register/helpers/register-response-code-helpers";
import {useHistory} from "react-router-dom";


export const CollectPolicyAndPostcode = () => {
    const PAGE_NAME = "collectPolicyPostcode";

    const locationValues = useLocation();

    const history = useHistory();

    const [lastNameState, setLastNameState] = useState<FieldModel>(createInitialFieldModel());
    const [dateOfBirthState, setDateOfBirthState] = useState<FieldModel>(createInitialFieldModel());
    const [dateOfBirthStateString, setDateOfBirthStateString] = useState<string>("");

    const [policyNumberState, setPolicyNumberState] = useState<FieldModel>(createInitialFieldModel());
    const [postcodeState, setPostcodeState] = useState<FieldModel>(createInitialFieldModel());

    const [pageStatus, setPageStatus] = useState<PageStatus>("Ready");
    const [pageModel, setPageModel] = useState<PageModel>(createInitialPageModel());

    const [userSession] = useState<UserSesionModel | null>(readSignInUserStorage());

    const [onLastNameChange, onLastNameBlur, invokeLastNameValidation] = createFieldHandlers(
        lastNameState,
        setLastNameState,
        createSurnameValidator()
    );
    const [onDateOfBirthChange, onDateOfBirthBlur, invokeDateOfBirthValidation] = createFieldHandlers(
        dateOfBirthState,
        setDateOfBirthState,
        createRequiredValidator("Date of Birth")
    );
    const [onPolicyNumberChange, onPolicyNumberBlur, invokePolicyNumberValidation] = createFieldHandlers(
        policyNumberState,
        setPolicyNumberState,
        createValidatePolicy("Policy number")
    );
    const [onPostcodeChange, onPostcodeBlur, invokePostcodeValidation] = createFieldHandlers(
        postcodeState,
        setPostcodeState,
        createPostcodeValidator("Postcode")
    );

    const errorReqHandler = (errorCode: string, errorMessage?: string) => {
        let [informationMessages, title] = createUIMessageAndTitleFromResponseCodes(errorCode, errorMessage);
        setPageModel(updatePageModelReducer(pageModel, {informationMessages, title}));
        setPageStatus("Failed");
    };

    const errorReqHandlerAWS = (errorCode: string, errorMessage?: string) => {
        let [informationMessages, title] = createUIMessageAndTitleFromResponseCodes(errorCode, errorMessage);
        if (errorMessage) {
            informationMessages = [errorMessage];
        }

        setPageModel(updatePageModelReducer(pageModel, {informationMessages, title}));
        setPageStatus("Failed");
    };

    useLayoutEffect(() => {
        if (!fromQueryParameters().doAllStandardExist()) {
            setPageStatus(() => "Missing Query Parameters");
        }
    }, []);

    useEffect(() => {
        document.title = "Complete your registration | Simplyhealth";
    }, []);

    useEffect(() => {
        if (locationValues.state) {
            let locationValState = locationValues.state as any;
            const fieldDOB: FieldModel = {
                value: prepareViewDateFormat(locationValState.dateOfBirth), messages: [], isTouched: false
            };
            setDateOfBirthStateString(locationValState.dateOfBirth);
            setDateOfBirthState(fieldDOB);

            const fieldLastName: FieldModel = {
                value: locationValState.lastName, messages: [], isTouched: false
            };
            setLastNameState(fieldLastName);
        }
    }, [locationValues]);

    useEffect(() => {
        sendPageVisitAnalytics(ANALYTICS_NAMES.collectDobLastname);
    }, []);

    useEffect(() => {
        if (pageStatus === "Redirect") {
            refreshSession(createGetCurrentLoginRequest()).then(response => {
                if (response.status === "succeeded" && userSession?.baseUrl) {
                    window.location.href = createRedirectUrl(
                        userSession?.baseUrl,
                        response.accessToken,
                        response.refreshToken,
                        response.idToken,
                        userSession.stateParams
                    )
                } else if (response.status === "succeeded") {
                  history.push("/registrationcomplete");
                } else {
                    errorReqHandlerAWS(response?.code, response?.message);
                }
            }).catch(error => {
                errorReqHandlerAWS(error?.code, error?.message);
            });
        } else {
            setPageModel(pm => updatePageModelForStatusChangeReducer(pm, pageStatus));
        }
    }, [pageStatus]);

    const getYearMonthAndDay = (dateInput: string): [number,string, string] => {
        var d = new Date(dateInput),
            month = '' + (d.getMonth() + 1),
            day = '' + d.getDate(),
            year = d.getFullYear();

        if (month.length < 2)
            month = '0' + month;
        if (day.length < 2)
            day = '0' + day;
        return [year, month, day];
    };

    const prepareServerDateFormat = (dateInput: string): string => {
        const [year, month, day] = getYearMonthAndDay(dateInput);
        return [year, month, day].join('-');
    };

    const prepareViewDateFormat = (dateInput: string): string => {
        const [year, month, day] = getYearMonthAndDay(dateInput);
        return [day, month, year].join('/');
    };


    const onSubmitClick = () => {
        switch (pageStatus) {
            case "Ready":
                sendRequest();
                break;
            case "Sending":
                // Do nothing
                break;
            case "Succeeded":
                // Do nothing
                break;
            case "Failed":
                setPageStatus("Ready");
                break;
        }
    };

    const sendRequest = () => {
        invokeLastNameValidation();
        invokeDateOfBirthValidation();
        invokePolicyNumberValidation();
        invokePostcodeValidation();
        if (areAllFieldsComplete() && userSession?.accessToken) {
            setPageStatus("Sending");
            const request = createCollectPolicyAndPostcodeRequest(
                userSession.email,
                prepareServerDateFormat(dateOfBirthStateString),
                lastNameState.value,
                policyNumberState.value,
                postcodeState.value,
                userSession.accessToken
            );

            checkCollectPolicyAndPostcode(request).then(response => {
                if (response.status === "succeeded" && response.clientId) {
                    setPageStatus("Redirect");
                } else {
                    if (response?.code.toLowerCase() === RESPONSE_CODES.SignUp_RegistrationError.toLowerCase()) {
                        errorReqHandler(response.code, response?.supportCode);
                    } else {
                        errorReqHandler(response.code, response?.supportCode);
                    }
                }
            }).catch(error => {
                errorReqHandler("");
            });
        }
    };

    const areAllFieldsComplete = () => {
        return areAllValid(
            lastNameState,
            dateOfBirthState,
            policyNumberState,
            postcodeState
        );
    };

    return (
        <Fragment>
            <FormContainer
                pageName={PAGE_NAME}
                title={pageModel.title}
                messages={pageModel.informationMessages}
                image={<img src={pageModel.titleImage} alt=""/>}
                fields={
                    isProcessing(pageStatus) ? (
                        <Fragment>
                            <Progress/>
                        </Fragment>
                    ) : isCompleted(pageStatus) ? (
                        <Fragment/>
                    ) : isErrored(pageStatus) ? (
                        <Fragment>
                            <ErrorMessages
                                name={createElementName(PAGE_NAME, "submit_errors", "errors")}
                                messages={pageModel.errorMessages}
                            />
                        </Fragment>
                    ) : (
                        <CollectPolicyAndPostcodeFieldSet
                            pageName={PAGE_NAME}
                            lastNameState={lastNameState}
                            onLastNameChange={onLastNameChange}
                            onLastNameBlur={onLastNameBlur}
                            dateOfBirthState={dateOfBirthState}
                            onDateOfBirthChange={onDateOfBirthChange}
                            onDateOfBirthBlur={onDateOfBirthBlur}
                            policyNoState={policyNumberState}
                            onPolicyNoChange={onPolicyNumberChange}
                            onPolicyNoBlur={onPolicyNumberBlur}
                            postcodeState={postcodeState}
                            onPostcodeChange={onPostcodeChange}
                            onPostcodeBlur={onPostcodeBlur}
                        />
                    )
                }
                action={
                    pageModel.showButton && (
                        <Button pageName={PAGE_NAME} name="submit" caption={pageModel.buttonTitle} onClick={onSubmitClick}/>
                    )
                }
                footer={
                    pageModel.showFooter && fromQueryParameters().doesRedirectUriExist() &&
                    <FormFooter message="Already have an account?" linkText="Login" redirectTo="/"/>
                }
            />
        </Fragment>
    );
};
